<template>
  <div class="ui-datepicker">
    <div class="ui-datepicker__actions">
      <div
        v-for="action in ACTIONS"
        :key="action.label"
        class="ui-datepicker__action"
        @click="action.action"
      >
        {{ action.label }}
      </div>
    </div>
    <div class="ui-datepicker__wrapper">
      <div class="ui-datepicker__header">
        <div class="ui-datepicker__header-area">
          <span
            :class="`ui-datepicker__header-month${
              isMonthSelect ? ' ui-datepicker__header-month--active' : ''
            }`"
            @click="handleIsMonthSelect()"
          >
            {{ displayedMonth.toFormat('MMMM') }}
          </span>
          <span
            :class="`ui-datepicker__header-year${
              isYearSelect ? ' ui-datepicker__header-year--active' : ''
            }`"
            @click="handleIsYearSelect()"
          >
            {{ displayedMonth.year }}
          </span>
        </div>
        <button
          v-if="isYearSelect || isMonthSelect"
          class="ui-datepicker__button ui-datepicker__button--close"
          type="button"
          @click="handleClosePanels"
        >
          <XMarkIcon aria-hidden="true" />
        </button>
        <div
          v-else
          class="ui-datepicker__navigation"
        >
          <button
            class="ui-datepicker__button ui-datepicker__button--arrow"
            data-refid="date-picker__prev-month"
            type="button"
            @click="handleClickPrev"
          >
            <ChevronLeftIcon aria-hidden="true" />
          </button>
          <button
            class="ui-datepicker__button ui-datepicker__button--arrow"
            data-refid="date-picker__next-month"
            type="button"
            @click="handleClickNext"
          >
            <ChevronRightIcon aria-hidden="true" />
          </button>
        </div>
      </div>
      <div
        v-if="isYearSelect"
        class="ui-datepicker__container ui-datepicker__container--years"
      >
        <div
          v-for="item in noOfYears"
          :key="item"
          class="ui-datepicker__year"
          :class="{
            'ui-datepicker__year--current': item === currentDate?.year,
            'ui-datepicker__year--selected': item === displayedMonth.year,
          }"
          @click="handleSetYear(item)"
        >
          {{ item }}
        </div>
      </div>
      <div
        v-else-if="isMonthSelect"
        class="ui-datepicker__container ui-datepicker__container--monthes"
      >
        <div
          v-for="(item, index) in MONTH_NAMES"
          :key="item"
          class="ui-datepicker__month"
          :class="{
            'ui-datepicker__month--current': index + 1 === currentDate?.month,
            'ui-datepicker__month--selected':
              index + 1 === displayedMonth.month,
          }"
          @click="handleSetMonth(index + 1)"
        >
          {{ item }}
        </div>
      </div>
      <template v-else>
        <div class="ui-datepicker__week">
          <div
            v-for="(day, index) in DAYS"
            :key="index"
            class="flex-1"
          >
            {{ day }}
          </div>
        </div>
        <div class="ui-datepicker__container">
          <div
            v-for="blankday in blankdays"
            :key="blankday"
          />
          <div
            v-for="(day, dateIndex) in noOfDays"
            :key="dateIndex"
            class="ui-datepicker__day"
            :class="{
              'ui-datepicker__day--today': isToday(day) && !isSelected(day),
              'ui-datepicker__day--selected': isSelected(day),
            }"
            @click="handleSelectDate(day)"
          >
            {{ day }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watchEffect } from 'vue'
import { DateTime } from 'luxon'

import { DAYS, MIN_DATE_YEAR, MONTH_NAMES } from '@/const/common'

import {
  isDate,
  dateTimeToString,
  getCurrentDate,
  getLastMonth,
  getLastWeek,
  getLastYear,
  stringToDateTime,
} from '@/helpers/dates'

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/vue/20/solid'

type Emits = {
  select: [string | null]
}
const emit = defineEmits<Emits>()

const modelValue = defineModel<string | null>({
  required: true,
})

const displayedMonth = ref(getCurrentDate().set({ day: 1 }))

const isYearSelect = ref(false)
const isMonthSelect = ref(false)

const ACTIONS = [
  {
    label: 'Today',
    action: () => handleSetDate('today'),
  },
  {
    label: 'Last week',
    action: () => handleSetDate('lastweek'),
  },
  {
    label: 'Last month',
    action: () => handleSetDate('lastmonth'),
  },
  {
    label: 'Last year',
    action: () => handleSetDate('lastyear'),
  },
]

const currentDate = computed(() => stringToDateTime(modelValue.value))

const previousValueAsDateTime = computed(() =>
  stringToDateTime(modelValue.value),
)

const noOfDays = computed<number[]>(() => {
  return [...Array(displayedMonth.value.daysInMonth).keys()].map(x => x + 1)
})

const blankdays = computed<number[]>(() => {
  const displayedMonthValue = displayedMonth.value.set({ day: 1 })
  return [...Array(displayedMonthValue.weekday).keys()].map(x => x + 1)
})

const noOfYears = Array(16)
  .fill('')
  .map((_, index) => getCurrentDate().year + index - 14)

const isToday = (day: number) => {
  return getCurrentDate().hasSame(displayedMonth.value.set({ day }), 'day')
}

const isSelected = (day: number) => {
  return stringToDateTime(modelValue.value)?.hasSame(
    displayedMonth.value.set({ day }),
    'day',
  )
}

const handleSelectDate = (day: number) => {
  const date = displayedMonth.value.set({ day })
  const result = dateTimeToString(date, { iso: true })
  handleUpdate(result)
}

const nextMonth = () => {
  displayedMonth.value = displayedMonth.value.plus({ months: 1 })
}

const prevMonth = () => {
  displayedMonth.value = displayedMonth.value.plus({ months: -1 })
}

const nextYear = () => {
  displayedMonth.value = displayedMonth.value.plus({ years: 1 })
}

const prevYear = () => {
  displayedMonth.value = displayedMonth.value.plus({ years: -1 })
}

const validateDate = (value?: DateTime) => {
  if (!value) return value
  if (value.year < MIN_DATE_YEAR) {
    value = value.set({ year: MIN_DATE_YEAR })
  }
  return value
}

const handleUpdate = (value: any) => {
  const date = stringToDateTime(value)
  if (
    previousValueAsDateTime.value &&
    date?.equals(previousValueAsDateTime.value)
  )
    return
  const result = validateDate(date)
  const resultString = dateTimeToString(result, { iso: true }) || null
  modelValue.value = resultString
  emit('select', resultString)
}

const handleClickPrev = () => {
  if ((event as KeyboardEvent).shiftKey) {
    prevYear()
  } else {
    prevMonth()
  }
}

const handleClickNext = () => {
  if ((event as KeyboardEvent).shiftKey) {
    nextYear()
  } else {
    nextMonth()
  }
}

const handleSetDate = (
  period: 'today' | 'lastweek' | 'lastmonth' | 'lastyear',
) => {
  let date = getCurrentDate()
  switch (period) {
    case 'lastweek':
      date = getLastWeek(date)
      break
    case 'lastmonth':
      date = getLastMonth(date)
      break
    case 'lastyear':
      date = getLastYear(date)
      break
  }
  const result = dateTimeToString(date, { iso: true })
  handleUpdate(result)
}

const handleIsMonthSelect = () => {
  isMonthSelect.value = !isMonthSelect.value
  isYearSelect.value = false
}

const handleIsYearSelect = () => {
  isYearSelect.value = !isYearSelect.value
  isMonthSelect.value = false
}

const handleClosePanels = () => {
  isYearSelect.value = false
  isMonthSelect.value = false
}

const handleSetYear = (item: number) => {
  displayedMonth.value = displayedMonth.value.set({ year: item })
  handleClosePanels()
}

const handleSetMonth = (item: number) => {
  displayedMonth.value = displayedMonth.value.set({ month: item })
  handleClosePanels()
}

watchEffect(() => {
  if (!modelValue.value || !isDate(modelValue.value)) return
  const date = stringToDateTime(modelValue.value)
  if (!date?.isValid) return
  displayedMonth.value = date
})
</script>

<style>
.ui-datepicker {
  @apply flex;
  @apply bg-white dark:bg-gray-800;

  &__actions {
    @apply p-2;
    @apply border-r border-gray-100 dark:border-gray-700;
    @apply text-sm sm:text-xs font-medium;
  }

  &__action {
    @apply p-2;
    @apply text-gray-500 hover:text-indigo-600;
    @apply dark:text-gray-400 dark:hover:text-indigo-400;
    @apply cursor-pointer;
  }

  &__wrapper {
    @apply w-72;
    @apply p-4;
  }

  &__header {
    @apply flex justify-between items-center;
    @apply mb-4;
  }

  &__header-area {
    @apply flex items-center gap-1;
  }

  &__header-month,
  &__header-year {
    @apply font-normal text-gray-600 hover:text-gray-800;
    @apply dark:text-gray-400 dark:hover:text-gray-200;
    @apply cursor-pointer;

    &--active {
      @apply font-bold text-gray-800 dark:text-gray-200;
    }
  }

  &__navigation {
    @apply flex items-center gap-1;
  }

  &__button {
    @apply h-5;
    @apply flex items-center;
    @apply px-2;
    @apply rounded-xl;
    @apply text-gray-500 hover:text-gray-700 focus:text-indigo-700;
    @apply dark:text-gray-400 dark:hover:text-gray-200 dark:focus:text-indigo-400;
    @apply bg-gray-50 hover:bg-gray-200 focus:bg-indigo-100;
    @apply dark:bg-gray-750 dark:hover:bg-gray-700 dark:focus:bg-indigo-500;
    @apply font-bold text-sm sm:text-xs uppercase;
    @apply cursor-pointer;

    svg {
      @apply size-5;
    }

    &--arrow,
    &--close {
      @apply w-5;
      @apply justify-center;
      @apply px-0;
    }

    &--arrow {
      @apply rounded-full;
    }

    &--close {
      @apply rounded-sm;
    }
  }

  &__container {
    @apply grid grid-cols-7 gap-2;
    @apply -mx-4 px-4;
    @apply text-center;

    &--years {
      @apply grid-cols-4;
    }

    &--monthes {
      @apply grid-cols-3;
    }
  }

  &__week {
    @apply h-5;
    @apply flex items-center;
    @apply gap-2 -mx-4 mb-2 px-4;
    @apply bg-gray-50 text-gray-400;
    @apply dark:bg-gray-750 dark:text-gray-300;
    @apply text-sm sm:text-xs text-center;
    @apply uppercase;
  }

  &__day,
  &__year,
  &__month {
    @apply flex items-center justify-center;
    @apply mx-auto;
    @apply text-gray-600 dark:text-gray-400;
    @apply border border-transparent;
    @apply rounded-full;
    @apply leading-loose;
    @apply cursor-pointer;

    &:not(&--selected) {
      @apply hover:text-gray-700 hover:bg-gray-100;
      @apply dark:hover:text-gray-300 dark:hover:bg-gray-700;
    }

    &--today:not(&--selected),
    &--current:not(&--selected) {
      @apply text-indigo-700 border-indigo-500;
    }

    &--selected {
      @apply bg-indigo-700 text-white;
      @apply dark:bg-indigo-500 dark:text-gray-50;
    }
  }

  &__day {
    @apply w-5 h-5;
    @apply text-sm sm:text-xs;
  }

  &__year,
  &__month {
    @apply h-6;
    @apply px-1;
    @apply rounded;
    @apply text-sm sm:text-xs;
  }
}
</style>
